import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus, type LucideIcon } from 'lucide-react';
import type { MarketingError } from '../api/errors';
import { ChannelErrorState, ChannelLoading } from './states';
import { formatCount } from '../model/format';
import { CAMPAIGN_STATUS_META } from '../model/status';
import { EmptyState } from '@/shared/components/empty-state';
import { EntityListCard, type EntityListCardBadge } from '@/shared/components/entity-list-card';
import { PageHeader } from '@/shared/components/page-header';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { formatDate, formatTime } from '@/shared/lib/format';
import { useSelection } from '@/shared/lib/use-selection';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Pagination } from '@/shared/ui/pagination';
import { EMPTY_CAMPAIGN_FILTERS, filterCampaignRows, type MarketingCampaignRow } from '../model/campaign-filters';
import { CampaignFilters } from './campaign-filters';

const PAGE_SIZE = 25;

function scheduleLabel(row: Pick<MarketingCampaignRow, 'status' | 'scheduledAt' | 'sendMode' | 'createdAt'>): string {
  const when = row.scheduledAt || row.createdAt;
  if (row.status === 'draft' || !when) return 'Sem data';
  if (row.sendMode === 'now') return 'Na aprovação';
  return `${formatDate(when)} ${formatTime(when)}`;
}

export function CampaignListPage({
  title,
  description,
  help,
  icon: Icon,
  phase,
  error = null,
  onRetry,
  loadingLabel,
  errorTitle,
  rows,
  emptyTitle,
  emptyDescription,
  filteredEmptyDescription = 'Troque o estado, o período ou limpe a busca.',
  selectAllId,
  banner,
  onCreate,
  onOpen,
  canOpen,
  renderActions,
}: {
  title: string;
  description?: string;
  help?: string;
  icon: LucideIcon;
  phase: 'loading' | 'error' | 'ready';
  /** Erro da API quando `phase` é `error`; sem ele o estado fica genérico. */
  error?: MarketingError | null;
  onRetry?: () => void;
  loadingLabel: string;
  errorTitle: string;
  rows: MarketingCampaignRow[];
  emptyTitle: string;
  emptyDescription: string;
  filteredEmptyDescription?: string;
  selectAllId: string;
  banner?: ReactNode;
  onCreate: () => void;
  onOpen: (row: MarketingCampaignRow) => void;
  /** Linha que não abre nada fica com o card sem clique. Sem isto, todo card chama `onOpen`. */
  canOpen?: (row: MarketingCampaignRow) => boolean;
  renderActions: (row: MarketingCampaignRow) => ReactNode;
}) {
  const [filters, setFilters] = useState(EMPTY_CAMPAIGN_FILTERS);
  const [pageIndex, setPageIndex] = useState(0);
  const filtered = useMemo(() => filterCampaignRows(rows, filters), [filters, rows]);
  const pageRows = useMemo(() => filtered.slice(pageIndex * PAGE_SIZE, pageIndex * PAGE_SIZE + PAGE_SIZE), [filtered, pageIndex]);
  const selectable = useMemo(() => pageRows.map((row) => ({ ...row, _id: row.id })), [pageRows]);
  const selection = useSelection(selectable);

  useEffect(() => {
    setPageIndex(0);
  }, [filters]);

  const header = (
    <PageHeader
      title={title}
      description={description}
      help={help}
      actions={
        <Button onClick={onCreate}>
          <Plus />
          <span className="hidden sm:inline">Nova campanha</span>
          <span className="sm:hidden">Nova</span>
        </Button>
      }
    />
  );

  if (phase === 'loading') {
    return (
      <>
        {header}
        <ChannelLoading label={loadingLabel} />
      </>
    );
  }

  if (phase === 'error') {
    return (
      <>
        {header}
        <ChannelErrorState title={errorTitle} error={error} onRetry={onRetry} />
      </>
    );
  }

  return (
    <div className="min-w-0">
      {header}
      {banner ? <div className="mb-4">{banner}</div> : null}
      {rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={Icon}
            title={emptyTitle}
            description={emptyDescription}
            action={
              <Button onClick={onCreate}>
                <Plus /> Nova campanha
              </Button>
            }
          />
        </Card>
      ) : (
        <SelectableListLayout
          search={filters.query}
          onSearchChange={(query) => setFilters((current) => ({ ...current, query }))}
          searchPlaceholder="Buscar por nome ou público"
          selectAllId={selectAllId}
          itemCount={pageRows.length}
          allOnPageSelected={selection.allOnPageSelected}
          someOnPageSelected={selection.someOnPageSelected}
          onToggleAllOnPage={selection.toggleAllOnPage}
          isEmpty={filtered.length === 0}
          emptyTitle="Nenhuma campanha com esse filtro"
          emptyDescription={filteredEmptyDescription}
          withFloatingBar={false}
          filtersRow={<CampaignFilters value={filters} onChange={setFilters} />}
          footer={
            filtered.length > PAGE_SIZE ? (
              <Pagination
                page={pageIndex}
                pageSize={PAGE_SIZE}
                total={filtered.length}
                onPageChange={setPageIndex}
                className="justify-center pt-2"
              />
            ) : null
          }
        >
          {pageRows.map((row, index) => {
            const meta = CAMPAIGN_STATUS_META[row.status];
            const badges: EntityListCardBadge[] = [
              {
                key: 'status',
                label: meta.label,
                variant: meta.variant,
                copyLines: [{ label: 'Estado', value: meta.label }],
              },
            ];
            if (row.eligible != null) {
              badges.push({
                key: 'volume',
                label: `${formatCount(row.eligible)} previstos`,
                variant: 'secondary',
                wide: true,
                copyLines: [{ label: 'Volume previsto', value: formatCount(row.eligible) }],
              });
            }
            return (
              <li key={row.id}>
                <EntityListCard
                  selected={selection.selectedIds.has(row.id)}
                  onToggle={() => selection.toggle(row.id)}
                  onCardClick={canOpen && !canOpen(row) ? null : () => onOpen(row)}
                  index={index}
                  ariaLabel={`Selecionar ${row.name}`}
                  title={row.name}
                  subtitle={<p className="truncate text-xs text-muted-foreground">{row.audienceName}</p>}
                  meta={
                    <p className="text-xs text-muted-foreground">
                      {scheduleLabel(row)}
                      {row.status === 'sending' && row.sent !== undefined ? ` · ${formatCount(row.sent)} já enviados` : ''}
                    </p>
                  }
                  thumbnail={null}
                  badges={badges}
                  actions={renderActions(row)}
                />
              </li>
            );
          })}
        </SelectableListLayout>
      )}
    </div>
  );
}
